/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B030020 請求状況照会
 */
import { Box } from "@mui/material";
import { Column, Query } from "@material-table/core";
import {
  SDateField,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  STextField,
  msgState,
} from "components";
import { labels } from "messages/labels";
import { createRef, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import { focusState, navigateTypeState, useFocus } from "states";
import { date, dateWithSlashAt, mapIds } from "../../services";
import { B000140, B030010InputData } from "pages";
import { useNavigate } from "react-router-dom";
import { B000140UrikakeshainNmr, catchRestError } from "clients/rest";
import {
  B030020Find,
  B030020FindRequest,
  B030020GetSeikyujoken,
} from "clients/rest/b030/B030020";
import { editKbn } from "services/codes";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// 検索条件を格納するデータタイプ
type SearchedFields = {
  searched: boolean;
} & Partial<{
  fields: Fields;
  page: number;
  pageSize: number;
  sort: string[];
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  seikyushimeYmd: string;
  urikakeshainCd: string;
  urikakeshainNmr: string;
}>;

// テーブルデータ定義
type Data = Partial<{
  gyoNo: number;
  seikyushimeshoriKbn: string;
  seikyushimeYmd: string;
  nyukinYmd: string;
  urikakeshainNm: string;
  updDate: string;
  updshainNm: string;
  seikyushimeNo: number;
  seikyushimeNoF: string;
  seikyushimeStatus: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  seikyushimeYmd: "",
  urikakeshainCd: "",
  urikakeshainNmr: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行　", width: "1.5rem", align: "right" },
  { field: "seikyushimeshoriKbn", title: "処理区分", width: "2rem" },
  {
    field: "seikyushimeYmd",
    title: "請求締日",
    width: "2rem",
    render: (data) => {
      if (data.seikyushimeYmd! !== null && data.seikyushimeYmd! !== "") {
        return dateWithSlashAt(data.seikyushimeYmd!);
      }
    },
  },
  {
    field: "nyukinYmd",
    title: "入金対象日",
    width: "2rem",
    render: (data) => {
      if (data.nyukinYmd! !== null && data.nyukinYmd! !== "") {
        return dateWithSlashAt(data.nyukinYmd!);
      }
    },
  },
  { field: "urikakeshainNm", title: "売掛担当者", width: "2.5rem" },
  { field: "updDate", title: "処理年月日(時間)", width: "3.5rem" },
  { field: "updshainNm", title: "オペレーター名", width: "2.5rem" },
  { field: "seikyushimeNo", hidden: true },
  { field: "seikyushimeNoF", title: "請求締番号", width: "3rem" },
  { field: "seikyushimeStatus", title: "処理状況", width: "4rem" },
];

const focusId = "B030020";

// B030020 請求状況照会
export const B030020 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

// 検索条件を格納するステート
const B030020State = atom<SearchedFields>({
  key: "B030020State",
  default: {
    searched: false,
    fields: defaultFields,
  },
});

const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // 1ページの行数
  const pageSize = 20;
  // テーブル行で選択されたデータ
  const [rowData, setRowData] = useState<Partial<Data>>();
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするRef
  const isDefaultPage = useRef(false);
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();
  const [menuNavigate, setMenuNavigate] = useRecoilState(navigateTypeState);
  // 詳細ボタン活性制御
  const [disabledDetailButton, setDisabledDetailButton] = useState(true);
  // 検索条件フィールドの定義
  const [searchedFields, setSearchedFields] = useRecoilState(B030020State);

  // 初期表示イベント
  useEffect(() => {
    if (menuNavigate === "menu") {
      (async () => {
        await methods.setValue("seikyushimeYmd", date());
        focus(1);
      })();
      return;
    }
    // 検索条件が保存されている
    if (searchedFields.searched) {
      const fields = searchedFields.fields;
      if (typeof fields !== "undefined") {
        methods.setValue("seikyushimeYmd", fields.seikyushimeYmd);
        methods.setValue("urikakeshainCd", fields.urikakeshainCd);
        methods.setValue("urikakeshainNmr", fields.urikakeshainNmr);
        setSearched(true);
        return;
      }
    }
  }, []);

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 初期化
    handleResetSearch();
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // 検索時の初期化処理
  const handleResetSearch = () => {
    // 検索実行時、選択行の解除と各ボタンの非活性化
    setRowData(undefined);
    setDisabledDetailButton(true);
    (tableRef.current as any).handleClearSelect();
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }

      // 検索フォームのデータを取得する。
      const fields = methods.getValues();

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B030020FindRequest = {
        seikyushimeYmd: fields.seikyushimeYmd!,
        urikakeshainCd: fields.urikakeshainCd,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        sort: ["seikyushimeNo,desc"],
      };
      // API呼び出し
      B030020Find(param)
        // 正常取得時
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        // エラー時
        .catch((reason) => {
          // 詳細ボタン非活性化
          setDisabledDetailButton(true);
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // 詳細ボタンクリックイベント
  const handleClickDatailButton = () => {
    if (typeof rowData === "undefined") {
      return;
    }
    // 入金データの検索を行う
    B030020GetSeikyujoken({
      seikyushimeNo: rowData.seikyushimeNo!.toString(),
    })
      .then((value) => {
        const inputData: B030010InputData = {
          editKbn: editKbn.reference,
          seikyu: value.data,
        };
        setSearchedFields({ searched: true, fields: methods.getValues() });
        setMenuNavigate("next");
        navigate("/b030010", { state: inputData });
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // テーブル行クリックイベント
  const handleSelectRow = (_: any, rowData: Data | undefined) => {
    setRowData(undefined);
    setRowData(rowData);
    setDisabledDetailButton(false);
  };

  // テーブル行ダブルクリック
  const handleDoubleClickRow = (data: Data) => {
    // 入金データの検索を行う
    B030020GetSeikyujoken({
      seikyushimeNo: data.seikyushimeNo!.toString(),
    })
      .then((value) => {
        const inputData: B030010InputData = {
          editKbn: editKbn.reference,
          seikyu: value.data,
        };
        setSearchedFields({ searched: true, fields: methods.getValues() });
        setMenuNavigate("next");
        navigate("/b030010", { state: inputData });
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="請求状況照会"
        gridTemplateColumns="6rem 10rem 2rem 6rem 8rem 20rem"
      >
        <SLabel required>請求締日</SLabel>
        <SDateField
          name="seikyushimeYmd"
          label="請求締日"
          required
          {...register(1)}
        />

        <Box />

        <SLabel>売掛担当者</SLabel>
        <SNumberField
          name="urikakeshainCd"
          label="売掛担当者"
          code
          size={7}
          dialog={<B000140 />}
          handleSubmit={(data: any) => {
            methods.setValue("urikakeshainCd", data.shainCd);
            methods.setValue("urikakeshainNmr", data.shainNm);
          }}
          onBlur={() => B000140UrikakeshainNmr(methods, navigate, setMsg)}
          {...register(2)}
        />
        <STextField
          name="urikakeshainNmr"
          label="売掛担当者名称"
          size={24}
          disabled
        />

        <Box gridColumn="span 5" />

        <SNormalButton
          label={labels.buttons.search}
          onClick={handleClickSearchButton}
          align="right"
          {...register(3)}
        />
      </SFrame>

      <SFrame>
        <STable
          keyName="gyoNo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          onRowClick={handleSelectRow}
          handleDoubleClick={handleDoubleClickRow}
          width={1280}
          height={550}
          pageSize={pageSize}
          onPageChange={handleResetSearch}
        />
      </SFrame>

      <SFrame>
        <SNormalButton
          label={labels.buttons.detail}
          onClick={handleClickDatailButton}
          disabled={disabledDetailButton}
          align="right"
          {...register(4)}
        />
      </SFrame>
    </FormProvider>
  );
};
